import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  IconButton,
  Typography,
  Button,
  Rating,
  Avatar,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper";

import { createSelector, Dispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { setChosenProduct, setRestaurant } from "./slice";
import { retrieveChosenProduct, retrieveRestaurant } from "./selector";
import { useParams, useHistory, Link } from "react-router-dom";
import ProductService from "../../services/ProductService";
import MemberService from "../../services/MemberService";
import { Member } from "../../../lib/types/member";
import { Product } from "../../../lib/types/product";
import { serverApi } from "../../../lib/config";
import { CartItem } from "../../../lib/types/search";
import { useGlobals } from "../../hooks/useGlobals";
import { sweetErrorHandling, sweetTopSmallSuccessAlert } from "../../../lib/sweetAlert";

const actionDispatch = (dispatch: Dispatch) => ({
  setRestaurant: (data: Member) => dispatch(setRestaurant(data)),
  setChosenProduct: (data: Product) => dispatch(setChosenProduct(data)),
});
const restaurantRetriever = createSelector(retrieveRestaurant, (restaurant) => ({ restaurant }));
const chosenProductRetriever = createSelector(retrieveChosenProduct, (chosenProduct) => ({ chosenProduct }));

interface ChosenProductPageProps {
  onAdd: (items: CartItem) => void;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const MOCK_REVIEWS = [
  {
    id: "1",
    name: "Sarah Jenkins",
    verified: true,
    date: "2 days ago",
    rating: 5,
    text: "Absolutely loved this book! The concept of the midnight library is so unique and thought-provoking. It really made me reflect on the choices I've made in life. A must-read for anyone who enjoys philosophical fiction.",
    helpful: 42,
    avatar: "SJ",
    color: "#22c55e",
  },
  {
    id: "2",
    name: "Michael Johnson",
    verified: true,
    date: "1 week ago",
    rating: 5,
    text: "One of the best books I've read this year. Matt Haig's writing is beautiful and the story is both heartwarming and profound. Highly recommend to everyone.",
    helpful: 15,
    avatar: "MJ",
    color: "#3b82f6",
  },
  {
    id: "3",
    name: "Amina Rahman",
    verified: true,
    date: "3 weeks ago",
    rating: 4,
    text: "Beautifully written and surprisingly comforting. The pacing is gentle, the message is powerful, and it stays with you after the last page. Perfect for a cozy weekend read.",
    helpful: 9,
    avatar: "AR",
    color: "#a855f7",
  },
];

const RATING_DISTRIBUTION = [
  { stars: 5, percent: 82 },
  { stars: 4, percent: 12 },
  { stars: 3, percent: 4 },
  { stars: 2, percent: 1 },
  { stars: 1, percent: 1 },
];

const MOCK_REVIEW_COUNT = 1240;
const MOCK_AVG_RATING = 4.8;

export default function ChosenProduct(props: ChosenProductPageProps) {
  const { onAdd } = props;
  const { productId } = useParams<{ productId: string }>();
  const history = useHistory();
  const { authMember } = useGlobals();
  const dispatch = useDispatch();
  const { setRestaurant, setChosenProduct } = useMemo(() => actionDispatch(dispatch), [dispatch]);
  const { chosenProduct } = useSelector(chosenProductRetriever);
  const { restaurant } = useSelector(restaurantRetriever);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  useEffect(() => {
    const productService = new ProductService();
    productService
      .getProduct(productId!)
      .then((data) => setChosenProduct(data))
      .catch((err) => console.log(err));

    const member = new MemberService();
    member.getRestaurant().then((data) => setRestaurant(data)).catch((err) => console.log(err));
  }, [productId, setChosenProduct, setRestaurant]);

  useEffect(() => {
    if (!chosenProduct) return;
    const productService = new ProductService();
    productService
      .getProducts({
        page: 1,
        limit: 10,
        order: "productLikes",
      })
      .then((data) => {
        const popular = data.filter((p) => p._id !== chosenProduct._id).slice(0, 6);
        setRelatedProducts(popular);
      })
      .catch(() => setRelatedProducts([]));
  }, [chosenProduct]);

  const refetchPopular = async (excludeId?: string) => {
    try {
      const productService = new ProductService();
      const data = await productService.getProducts({ page: 1, limit: 10, order: "productLikes" });
      const popular = data.filter((p) => p._id !== excludeId).slice(0, 6);
      setRelatedProducts(popular);
    } catch (err) {
      console.log(err);
    }
  };

  const likeProductHandler = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!id) return;
    if (!authMember) {
      await sweetErrorHandling({ message: "Please log in to like products." });
      return;
    }
    try {
      const productService = new ProductService();
      await productService.likeProduct(id);
      await sweetTopSmallSuccessAlert("Success", 800);
      // Refresh chosen product only if we're liking this product
      if (chosenProduct?._id === id) {
        const fresh = await productService.getProduct(id);
        setChosenProduct(fresh);
      }
      // Refresh popular list to reflect counts
      await refetchPopular(chosenProduct?._id ?? id);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddToBasket = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAdd({
      _id: chosenProduct!._id,
      quantity: 1,
      name: chosenProduct!.productName,
      price: chosenProduct!.productPrice,
      image: chosenProduct!.productImages[0],
    });
  };

  if (!chosenProduct) return null;

  const imagePath = (img: string) => `${serverApi}/${img}`;
  const isLiked = chosenProduct?.meLiked?.[0]?.myFavorite ?? false;
  const author = chosenProduct.productAuthor || restaurant?.memberNick || "";
  const ratingPercent = (MOCK_AVG_RATING / 5) * 100;
  const getCollectionSlug = (collection?: string) =>
    (collection ?? "other").toLowerCase().replaceAll("_", "-");

  const getLanguageLabel = (lang: unknown) => {
    const raw = String(lang ?? "").trim();
    if (!raw) return "—";
    const normalized = raw.toUpperCase();
    if (normalized === "ENGLISH") return "English";
    if (normalized === "RUSSIAN") return "Russian";
    if (normalized === "KOREAN") return "Korean";
    return raw.replaceAll("_", " ");
  };

  return (
    <div className="chosen-product chosen-product--lux">
      <div className="chosen-product__back-wrap">
        <Container maxWidth="lg">
          <button
            type="button"
            className="chosen-product__back"
            onClick={() => history.goBack()}
            aria-label="Back"
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
            <span>Back</span>
          </button>
        </Container>
      </div>

      <section className="chosen-product__hero">
        <Container maxWidth="lg">
          <div className="chosen-product__hero-shell">
            <div className="chosen-product__hero-grid">
              <div className="chosen-product__gallery-card">
                <Swiper
                  loop={chosenProduct.productImages.length > 1}
                  spaceBetween={12}
                  navigation
                  modules={[Navigation]}
                  className="chosen-product__swiper"
                >
                  {chosenProduct.productImages.map((img: string, index: number) => (
                    <SwiperSlide key={index}>
                      <div className="chosen-product__slide">
                        <img src={imagePath(img)} alt={`${chosenProduct.productName} ${index + 1}`} />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              <div className="chosen-product__info-card">
                <div className="chosen-product__meta-row">
                  <span className="chosen-product__meta-pill">
                    <RemoveRedEyeIcon sx={{ fontSize: 16 }} /> {formatCount(chosenProduct.productViews)} views
                  </span>
                  <span className="chosen-product__meta-pill chosen-product__meta-pill--likes">
                    <FavoriteIcon sx={{ fontSize: 16 }} /> {formatCount(chosenProduct.productLikes)} likes
                  </span>
                  <span className="chosen-product__meta-spacer" />
                  <IconButton
                    className={`chosen-product__wishlist ${isLiked ? "chosen-product__wishlist--active" : ""}`}
                    onClick={(e) => likeProductHandler(e, chosenProduct._id)}
                    aria-label={isLiked ? "Remove from favorites" : "Add to favorites"}
                    size="medium"
                  >
                    {isLiked ? (
                      <FavoriteIcon sx={{ color: "#e53935" }} />
                    ) : (
                      <FavoriteBorderIcon sx={{ color: "#94a3b8" }} />
                    )}
                  </IconButton>
                </div>

                <Typography component="h1" className="chosen-product__title">
                  {chosenProduct.productName}
                </Typography>
                {author && <p className="chosen-product__author">by {author}</p>}

                <div className="chosen-product__rating-row">
                  <div className="chosen-product__rating-ring" aria-hidden>
                    <svg viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="4" opacity="0.18" />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeDasharray={`${ratingPercent * 1.76} 176`}
                        strokeLinecap="round"
                        transform="rotate(-90 32 32)"
                      />
                    </svg>
                    <span className="chosen-product__rating-num">{MOCK_AVG_RATING}</span>
                  </div>
                  <div className="chosen-product__rating-meta">
                    <Rating value={MOCK_AVG_RATING} precision={0.1} readOnly size="small" />
                    <span className="chosen-product__rating-count">
                      {MOCK_REVIEW_COUNT.toLocaleString()} reviews
                    </span>
                  </div>
                </div>

                <p className="chosen-product__desc">
                  {chosenProduct.productDesc || "No description available."}
                </p>

                <div className="chosen-product__specs-row" aria-label="Product details">
                  <div className="chosen-product__spec">
                    <span className="chosen-product__spec-k">Published</span>
                    <span className="chosen-product__spec-v">
                      {chosenProduct.productYear ? new Date(chosenProduct.productYear).getFullYear() : "—"}
                    </span>
                  </div>
                  <div className="chosen-product__spec">
                    <span className="chosen-product__spec-k">Format</span>
                    <span className="chosen-product__spec-v">
                      {chosenProduct.productFormat ? String(chosenProduct.productFormat).replaceAll("_", " ") : "—"}
                    </span>
                  </div>
                  <div className="chosen-product__spec">
                    <span className="chosen-product__spec-k">Language</span>
                    <span className="chosen-product__spec-v">
                      {chosenProduct.productLanguage ? getLanguageLabel(chosenProduct.productLanguage) : "—"}
                    </span>
                  </div>
                  <div className="chosen-product__spec">
                    <span className="chosen-product__spec-k">Pages</span>
                    <span className="chosen-product__spec-v">
                      {typeof chosenProduct.productPages === "number" ? chosenProduct.productPages : "—"}
                    </span>
                  </div>
                </div>

                <div className="chosen-product__buybar">
                  <div className="chosen-product__price">${chosenProduct.productPrice.toFixed(2)}</div>
                  <div className="chosen-product__actions">
                    <Button
                      variant="contained"
                      className="chosen-product__btn chosen-product__btn--primary"
                      startIcon={<ShoppingCartOutlinedIcon />}
                      onClick={handleAddToBasket}
                    >
                      Add to basket
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* What readers say */}
      <section className="chosen-product__reviews">
        <Container maxWidth="lg">
          <h2 className="chosen-product__section-title">What readers say</h2>
          <div className="chosen-product__reviews-layout chosen-product__reviews-layout--exclusive">
            {/* Left: modern summary panel */}
            <aside className="chosen-product__reviews-panel">
              <div className="chosen-product__reviews-panel-top">
                <div className="chosen-product__reviews-score">
                  <div className="chosen-product__reviews-score-num">{MOCK_AVG_RATING}</div>
                  <Rating value={MOCK_AVG_RATING} precision={0.1} readOnly size="small" />
                  <div className="chosen-product__reviews-score-sub">
                    {MOCK_REVIEW_COUNT.toLocaleString()} ratings
                  </div>
                </div>

                <div className="chosen-product__reviews-stats">
                  <div className="chosen-product__reviews-stat">
                    <div className="chosen-product__reviews-stat-k">Most loved</div>
                    <div className="chosen-product__reviews-stat-v">5★ {RATING_DISTRIBUTION[0].percent}%</div>
                  </div>
                  <div className="chosen-product__reviews-stat">
                    <div className="chosen-product__reviews-stat-k">Recommended</div>
                    <div className="chosen-product__reviews-stat-v">92%</div>
                  </div>
                  <div className="chosen-product__reviews-stat">
                    <div className="chosen-product__reviews-stat-k">Quality</div>
                    <div className="chosen-product__reviews-stat-v">Premium</div>
                  </div>
                </div>
              </div>

              <div className="chosen-product__rating-bars chosen-product__rating-bars--panel">
                {RATING_DISTRIBUTION.map(({ stars, percent }) => (
                  <div key={stars} className="chosen-product__rating-bar-row">
                    <span>{stars}</span>
                    <div className="chosen-product__rating-bar-track">
                      <div className="chosen-product__rating-bar-fill" style={{ width: `${percent}%` }} />
                    </div>
                    <span className="chosen-product__rating-bar-pct">{percent}%</span>
                  </div>
                ))}
              </div>
            </aside>

            {/* Right: featured review + 2 cards */}
            <div className="chosen-product__reviews-right">
              <div className="chosen-product__reviews-actions">
                <Button
                  variant="outlined"
                  className="chosen-product__write-review"
                  startIcon={<EditOutlinedIcon />}
                >
                  Write a review
                </Button>
              </div>

              <article className="chosen-product__review-featured">
                <div className="chosen-product__review-header">
                  <Avatar sx={{ width: 48, height: 48, bgcolor: MOCK_REVIEWS[0].color, fontSize: 15, fontWeight: 700 }}>
                    {MOCK_REVIEWS[0].avatar}
                  </Avatar>
                  <div className="chosen-product__review-meta">
                    <span className="chosen-product__review-name">{MOCK_REVIEWS[0].name}</span>
                    <span className="chosen-product__review-verified">{MOCK_REVIEWS[0].date}</span>
                  </div>
                </div>
                <div className="chosen-product__review-featured-row">
                  <Rating value={MOCK_REVIEWS[0].rating} readOnly size="small" className="chosen-product__review-stars" />
                  <span className="chosen-product__review-featured-tag">Editor’s pick</span>
                </div>
                <p className="chosen-product__review-text">{MOCK_REVIEWS[0].text}</p>
                <div className="chosen-product__review-footer">
                  <button type="button" className="chosen-product__review-helpful">
                    <ThumbUpOutlinedIcon sx={{ fontSize: 18 }} />
                    Helpful ({MOCK_REVIEWS[0].helpful})
                  </button>
                  <button type="button" className="chosen-product__review-report">Report</button>
                </div>
              </article>

              <div className="chosen-product__reviews-grid">
                {MOCK_REVIEWS.slice(1, 3).map((review) => (
                  <article key={review.id} className="chosen-product__review-card">
                    <div className="chosen-product__review-header">
                      <Avatar sx={{ width: 44, height: 44, bgcolor: review.color, fontSize: 15, fontWeight: 700 }}>
                        {review.avatar}
                      </Avatar>
                      <div className="chosen-product__review-meta">
                        <span className="chosen-product__review-name">{review.name}</span>
                        <span className="chosen-product__review-verified">{review.date}</span>
                      </div>
                    </div>
                    <Rating value={review.rating} readOnly size="small" className="chosen-product__review-stars" />
                    <p className="chosen-product__review-text">{review.text}</p>
                    <div className="chosen-product__review-footer">
                      <button type="button" className="chosen-product__review-helpful">
                        <ThumbUpOutlinedIcon sx={{ fontSize: 18 }} />
                        Helpful ({review.helpful})
                      </button>
                      <button type="button" className="chosen-product__review-report">Report</button>
                    </div>
                  </article>
                ))}
              </div>

              <Button variant="outlined" className="chosen-product__load-reviews">
                Load more reviews
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* More to love */}
      <section className="chosen-product__also-bought">
        <Container maxWidth="lg">
          <div className="chosen-product__also-bought-head">
            <div className="chosen-product__also-title-wrap">
              <h2 className="chosen-product__section-title chosen-product__section-title--love">
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                More to Love
              </h2>
              <p className="chosen-product__section-subtitle">
                Popular picks readers can’t stop talking about.
              </p>
            </div>
            <Link to="/products" className="chosen-product__view-all">
              View all
              <ArrowForwardIcon sx={{ fontSize: 20 }} />
            </Link>
          </div>
          <div className="chosen-product__also-bought-scroll">
            {relatedProducts.slice(0, 6).map((product) => (
              <article
                key={product._id}
                className={`user-favourite-card chosen-product__also-fav-card product-card product-card-book product-card--${getCollectionSlug(
                  String(product.productCollection ?? "other"),
                )}`}
                onClick={() => history.push(`/products/${product._id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && history.push(`/products/${product._id}`)}
              >
                <div className="user-favourite-card-image-wrap">
                  <img
                    src={imagePath(product.productImages?.[0] || "")}
                    alt={product.productName}
                    className="user-favourite-card-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/icons/noimage-list.svg";
                    }}
                  />
                  <div className={`user-favourite-card-badges user-favourite-card-badges--${getCollectionSlug(String(product.productCollection ?? "other"))}`}>
                    <span className="user-favourite-card-badge">
                      {String(product.productCollection ?? "OTHER").replaceAll("_", " ").toUpperCase()}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="user-favourite-card-add-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdd({
                        _id: product._id,
                        quantity: 1,
                        name: product.productName,
                        price: product.productPrice,
                        image: product.productImages?.[0] ?? "",
                      });
                      sweetTopSmallSuccessAlert("Success", 800);
                    }}
                  >
                    <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                    Add to basket
                  </button>
                </div>
                <Typography variant="subtitle1" className="product-card-title">
                  {product.productName}
                </Typography>
                {product.productAuthor && (
                  <Typography variant="body2" color="text.secondary" className="product-card-author">
                    {product.productAuthor}
                  </Typography>
                )}
                <div className="product-card-divider" />
                <div className="product-card-footer">
                  <div className="product-card-footer-left">
                    <Typography variant="body1" className="product-card-price">
                      ${product.productPrice.toFixed(2)}
                    </Typography>
                  </div>
                  <div className="product-card-footer-divider" />
                  <div className="product-card-footer-stats">
                    <span className="product-card-views">
                      <RemoveRedEyeIcon sx={{ fontSize: 22 }} />
                      <span className="product-card-stat-num">{product.productViews ?? 0}</span>
                    </span>
                    <button
                      type="button"
                      className="product-card-likes"
                      onClick={(e) => likeProductHandler(e, product._id)}
                      aria-label={product?.meLiked?.[0]?.myFavorite ? "Unlike" : "Like"}
                    >
                      {product?.meLiked?.[0]?.myFavorite ? (
                        <FavoriteIcon sx={{ fontSize: 22, color: "#e53935" }} />
                      ) : (
                        <FavoriteBorderIcon sx={{ fontSize: 22, color: "#94a3b8" }} />
                      )}
                      <span className="product-card-stat-num">{product.productLikes ?? 0}</span>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
